/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'

// Chakra Components
import {
    Box,
    Fade,
    Flex,
    Stack,
    Text,
    VisuallyHidden
} from '@salesforce/retail-react-app/app/components/shared/ui'
import colors from '../../theme/foundations/colors'

// Project Components
import {HideOnDesktop, HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive'
import ItemVariantProvider from '../item-variant'
import CartItemVariantImage from '../item-variant/item-image'
import CartItemVariantName from '../item-variant/item-name'
import CartItemVariantAttributes from '../item-variant/item-attributes'
import CartItemVariantPrice from '../item-variant/item-price'
import LoadingSpinner from '@app/components/loading-spinner'
import QuantityPicker from '../quantity-picker'

// Utilities
import {noop} from '@salesforce/retail-react-app/app/utils/utils'

// Hooks
import {useCurrency, useDerivedProduct} from '@salesforce/retail-react-app/app/hooks'
import {useStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui'

/**
 * Component representing a product item usually in a list with details about the product - name, variant, pricing, etc.
 * @param {Object} product Product to be represented in the list item.
 * @param {node} primaryAction Child component representing the most prominent action to be performed by the user.
 * @param {node} secondaryActions Child component representing the other actions relevant to the product to be performed by the user.
 * @param {func} onItemQuantityChange callback function to be invoked whenever item quantity changes.
 * @param {boolean} showLoading Renders a loading spinner with overlay if set to true.
 * @param {boolean} isCart Tells if the component is used on cart, defaults to false
 * @param {boolean} hasError Flag to indicate if the product item has an error on quantity update
 * @returns A JSX element representing product item in a list (eg: wishlist, cart, etc).
 */
const ProductItem = ({
    product,
    primaryAction,
    secondaryActions,
    tertiaryActions,
    onItemQuantityChange = noop,
    showLoading = false,
    isCart = false,
    hasError = false
}) => {
    const {stepQuantity, setQuantity} = useDerivedProduct(product)
    const {currency: activeCurrency} = useCurrency()
    const intl = useIntl()
    const styles = useStyleConfig('Cart')

    return (
        <Box
            position="relative"
            data-testid={`sf-cart-item-${product?.productId ? product?.productId : product?.id}`}
            role="group"
            aria-label={intl.formatMessage({
                defaultMessage: 'Product',
                id: 'add_to_cart_modal.aria_label.product'
            })}
        >
            <ItemVariantProvider variant={product}>
                {showLoading && <LoadingSpinner />}
                <Stack {...styles.cardContainer}>
                    <Flex width="full" alignItems="flex-start">
                        <CartItemVariantImage {...styles.cardImage} mr={[16, null, 24]} />
                        <Stack spacing={3} flex={1}>
                            <Flex align="flex-start" justify="space-between">
                                <Stack spacing={1}>
                                    <CartItemVariantName {...styles.cardHeading} />
                                    <HideOnDesktop>
                                        <Box marginTop={8}>
                                            <CartItemVariantPrice
                                                align="left"
                                                currency={activeCurrency}
                                            />
                                        </Box>
                                    </HideOnDesktop>
                                    <CartItemVariantAttributes mt="12" />
                                </Stack>
                                <Stack ml={[0, null, 24]} flex={['auto', null, null, '1 0 auto']}>
                                    <HideOnMobile>
                                        <CartItemVariantPrice currency={activeCurrency} />
                                    </HideOnMobile>
                                    <Box display={['none', 'block', 'block', 'block']}>
                                        {primaryAction}
                                    </Box>
                                </Stack>
                            </Flex>

                            <Stack spacing={4} mt="12">
                                <Text
                                    variant="bodySmall"
                                    color="darkGray"
                                    aria-label={intl.formatMessage(
                                        {
                                            id: 'item_variant.quantity.label',
                                            defaultMessage:
                                                'Quantity selector for {productName}. Selected quantity is {quantity}'
                                        },
                                        {
                                            quantity: product?.quantity,
                                            productName: product?.name
                                        }
                                    )}
                                >
                                    <FormattedMessage
                                        defaultMessage="Quantity:"
                                        id="product_item.label.quantity"
                                    />
                                </Text>

                                <Stack
                                    direction={{base: 'column', lg: 'row'}}
                                    alignItems={['flex-start', null, null, 'center']}
                                >
                                    <QuantityPicker
                                        step={stepQuantity}
                                        value={product?.quantity}
                                        min={0}
                                        clampValueOnBlur={false}
                                        onBlur={(e) => {
                                            // Default to last known quantity if a user leaves the box with an invalid value
                                            const {value} = e.target

                                            if (!value) {
                                                setQuantity(product.quantity)
                                            }
                                        }}
                                        onChange={(stringValue, numberValue) => {
                                            // Set the Quantity of product to value of input if value number
                                            if (numberValue >= 0) {
                                                // Call handler
                                                onItemQuantityChange(numberValue).then(
                                                    (isValidChange) =>
                                                        isValidChange && setQuantity(numberValue)
                                                )
                                            } else if (stringValue === '') {
                                                // We want to allow the use to clear the input to start a new input so here we set the quantity to '' so NAN is not displayed
                                                // User will not be able to add '' quantity to the cart due to the add to cart button enablement rules
                                                setQuantity(stringValue)
                                            }
                                        }}
                                        productName={product?.name}
                                    />
                                    {secondaryActions}
                                </Stack>
                                <Box>
                                    {product && hasError && isCart && (
                                        <Fade in={true}>
                                            <Text
                                                color={colors.red}
                                                display="block"
                                                variant={'bodyBase2'}
                                            >
                                                {intl.formatMessage(
                                                    {
                                                        defaultMessage: 'Only {stockLevel} left!',
                                                        id: 'use_product.message.inventory_remaining'
                                                    },
                                                    {stockLevel: product.quantity}
                                                )}
                                            </Text>
                                        </Fade>
                                    )}
                                </Box>
                                <VisuallyHidden>
                                    <output aria-live="polite">
                                        {product?.name}
                                        {intl.formatMessage(
                                            {
                                                id: 'item_variant.assistive_msg.quantity',
                                                defaultMessage: 'Quantity {quantity}'
                                            },
                                            {
                                                quantity: product?.quantity
                                            }
                                        )}
                                    </output>
                                </VisuallyHidden>
                                <HideOnMobile>{tertiaryActions}</HideOnMobile>
                            </Stack>
                        </Stack>
                    </Flex>
                    <HideOnDesktop w={'full'}>{tertiaryActions}</HideOnDesktop>

                    <Box display={['block', 'none', 'none', 'none']} w={'full'}>
                        {primaryAction}
                    </Box>
                </Stack>
            </ItemVariantProvider>
        </Box>
    )
}

ProductItem.propTypes = {
    product: PropTypes.object,
    onItemQuantityChange: PropTypes.func,
    showLoading: PropTypes.bool,
    primaryAction: PropTypes.node,
    secondaryActions: PropTypes.node,
    tertiaryActions: PropTypes.node,
    isCart: PropTypes.bool,
    hasError: PropTypes.bool
}

export default ProductItem
